'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  NAVIGATION_CONTEXTS,
  NAVIGATION_CONTEXT_ORDER,
  type NavigationContext,
  type ResolvedNavigationSection,
} from '@/src/lib/navigation';
import { setActiveContextAction } from '@/src/server/actions/set-active-context.action';
import { useWorkflowStep } from '@/src/lib/sidebar-workflow';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--k-color-surface)]';

const COLLAPSE_THRESHOLD = 4;

export interface MobileNavProps {
  sections: ResolvedNavigationSection[];
  activeContext: NavigationContext;
  availableContexts: readonly NavigationContext[];
  powerMode: boolean;
}

export function MobileNav({
  sections,
  activeContext,
  availableContexts,
  powerMode,
}: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const workflow = useWorkflowStep();

  const router = useRouter();

  function handleSelect(context: NavigationContext) {
    if (context === activeContext) {
      setOpen(false);
      return;
    }
    setIsPending(true);
    const fd = new FormData();
    fd.set('context', context);
    void setActiveContextAction(fd).then((result) => {
      if (result.ok) router.push(NAVIGATION_CONTEXTS[context].href);
    }).finally(() => {
      setIsPending(false);
      setOpen(false);
    });
  }

  const visibleContexts = NAVIGATION_CONTEXT_ORDER.filter((ctx) =>
    availableContexts.includes(ctx),
  );

  return (
    <>
      <button
        type="button"
        aria-label="Navigation öffnen"
        onClick={() => setOpen(true)}
        className={`inline-flex h-10 w-10 items-center justify-center rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] text-[var(--k-color-text-secondary)] shadow-[var(--k-shadow-xs)] transition duration-150 hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] lg:hidden ${FOCUS_RING}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      {open ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Navigation schließen"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-slate-900/40"
          />
          <div className="relative h-full w-[min(20rem,90vw)] overflow-y-auto bg-[var(--k-color-surface)] shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-5 py-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-[var(--k-radius-md)] bg-[var(--k-color-accent)] text-sm font-bold text-white">
                  K
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
                    Kontora
                  </p>
                  <h2 className="text-sm font-semibold text-[var(--k-color-text-primary)]">
                    Navigation
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Schließen"
                className={`inline-flex h-9 w-9 items-center justify-center rounded-[var(--k-radius-md)] bg-[var(--k-color-surface-muted)] text-[var(--k-color-text-secondary)] transition duration-150 hover:bg-[var(--k-color-bg-canvas)] hover:text-[var(--k-color-text-primary)] ${FOCUS_RING}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <nav
              aria-label="Mobile Hauptnavigation"
              className="space-y-3 px-3 py-3"
            >
              <section aria-labelledby="mobile-context-heading" className="space-y-1.5">
                <h3
                  id="mobile-context-heading"
                  className="px-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]"
                >
                  Kontext
                </h3>
                <div className="grid grid-cols-2 gap-1">
                  {visibleContexts.map((context) => {
                    const meta = NAVIGATION_CONTEXTS[context];
                    const isActive = context === activeContext;
                    return (
                      <button
                        key={context}
                        type="button"
                        onClick={() => handleSelect(context)}
                        disabled={isPending}
                        aria-pressed={isActive}
                        className={[
                          `flex items-center gap-2 rounded-[var(--k-radius-sm)] border px-2.5 py-1.5 text-left text-xs font-semibold transition duration-150 disabled:opacity-60 ${FOCUS_RING}`,
                          isActive
                            ? 'border-[var(--k-color-accent)] bg-[var(--k-color-surface-muted)] text-[var(--k-color-text-link)]'
                            : 'border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] text-[var(--k-color-text-secondary)] hover:border-[var(--k-color-border-default)] hover:text-[var(--k-color-text-primary)]',
                        ].join(' ')}
                      >
                        <span aria-hidden="true" className="text-sm">
                          {meta.icon}
                        </span>
                        <span className="truncate">{meta.shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
                {powerMode ? (
                  <p className="mx-2 mt-1 inline-flex items-center gap-1 rounded-md bg-[var(--k-color-area-system-soft)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--k-color-role-system-text)]">
                    <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--k-color-role-system-icon)]" />
                    Power-Mode aktiv
                  </p>
                ) : null}
              </section>

              {activeContext === 'accounting' ? (
                <MobileWorkflowStrip
                  steps={workflow.steps}
                  activeStepId={workflow.activeStepId}
                />
              ) : null}

              {sections.length === 0 ? (
                <p className="px-2 text-xs text-[var(--k-color-text-muted)]">
                  Keine Einträge im aktuellen Kontext.
                </p>
              ) : (
                sections.map((section) => (
                  <MobileSection key={section.label} section={section} onNavigate={() => setOpen(false)} />
                ))
              )}
            </nav>
          </div>
        </div>
      ) : null}
    </>
  );
}

function MobileSection({
  section,
  onNavigate,
}: {
  section: ResolvedNavigationSection;
  onNavigate: () => void;
}) {
  const visibleGroups = section.groups.filter((g) => g.items.length > 0);
  if (visibleGroups.length === 0) return null;

  return (
    <section
      aria-label={section.label}
      className="space-y-2 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-2 py-2"
    >
      <div className="flex items-center gap-2 px-1">
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[var(--k-radius-sm)] bg-[var(--k-color-surface)] text-[10px] font-bold text-[var(--k-color-text-muted)]"
          aria-hidden="true"
        >
          {section.icon}
        </span>
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--k-color-text-muted)]">
          {section.label}
        </h2>
      </div>
      <div className="space-y-1.5">
        {visibleGroups.map((group) => (
          <MobileGroup key={group.label} group={group} onNavigate={onNavigate} />
        ))}
      </div>
    </section>
  );
}

function MobileGroup({
  group,
  onNavigate,
}: {
  group: ResolvedNavigationSection['groups'][number];
  onNavigate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const total = group.items.length;
  const isOverflow = total > COLLAPSE_THRESHOLD;
  const containsActive = group.items.some((i) => i.isActive);
  const showAll = !isOverflow || expanded || containsActive;
  const visibleItems = showAll ? group.items : group.items.slice(0, COLLAPSE_THRESHOLD);
  const hiddenCount = total - COLLAPSE_THRESHOLD;

  return (
    <div className="rounded-[var(--k-radius-sm)] bg-[var(--k-color-surface)] px-1.5 py-1.5">
      <div className="flex items-center justify-between gap-1 px-1">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--k-color-text-faint)]">
          {group.label}
        </h3>
        {isOverflow ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={showAll}
            aria-label={
              showAll
                ? `${group.label} einklappen`
                : `${group.label}: ${hiddenCount} weitere anzeigen`
            }
            className={`flex h-5 w-5 items-center justify-center rounded text-[var(--k-color-text-muted)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] ${FOCUS_RING}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`transition-transform duration-150 ${showAll ? 'rotate-180' : ''}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        ) : null}
      </div>
      <ul className="mt-0.5 space-y-0.5">
        {visibleItems.map((item) => {
          if (!item.href || item.isDisabled) {
            if (item.isComingSoon) {
              return (
                <li
                  key={item.label}
                  className="flex items-center justify-between rounded-[var(--k-radius-sm)] px-2 py-1 text-[12px] text-[var(--k-color-text-faint)]"
                >
                  <span className="truncate">{item.label}</span>
                  <span className="rounded-full bg-[var(--k-color-bg-canvas)] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--k-color-text-muted)]">
                    Neu
                  </span>
                </li>
              );
            }
            return null;
          }
          return (
            <li key={item.label}>
              <Link
                href={item.href}
                onClick={onNavigate}
                aria-current={item.isActive ? 'page' : undefined}
                className={`flex items-center justify-between gap-2 rounded-[var(--k-radius-sm)] px-2 py-1 text-[13px] transition ${
                  item.isActive
                    ? 'bg-[var(--k-color-surface-sidebar-active)] font-semibold text-[var(--k-color-text-link)]'
                    : 'text-[var(--k-color-text-secondary)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]'
                } ${FOCUS_RING}`}
              >
                <span className="truncate">{item.label}</span>
                {item.isActive ? (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--k-color-accent)]"
                  />
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
      {isOverflow && !showAll ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          className={`mt-0.5 flex w-full items-center justify-center gap-1 rounded-[var(--k-radius-sm)] px-2 py-1 text-[11px] font-medium text-[var(--k-color-text-muted)] transition duration-150 hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-link)] ${FOCUS_RING}`}
        >
          <span>Mehr anzeigen</span>
          <span className="rounded-full bg-[var(--k-color-bg-canvas)] px-1.5 py-0.5 text-[9px] font-bold text-[var(--k-color-text-muted)]">
            +{hiddenCount}
          </span>
        </button>
      ) : null}
    </div>
  );
}

function MobileWorkflowStrip({
  steps,
  activeStepId,
}: {
  steps: ReturnType<typeof useWorkflowStep>['steps'];
  activeStepId: ReturnType<typeof useWorkflowStep>['activeStepId'];
}) {
  const activeIdx = activeStepId ? steps.findIndex((s) => s.id === activeStepId) : -1;
  return (
    <section
      aria-label="Buchhaltungs-Workflow"
      data-tour="workflow-timeline-mobile"
      className="rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-2 py-2"
    >
      <div className="mb-1.5 flex items-center justify-between px-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--k-color-text-muted)]">
          Workflow
        </p>
        {activeIdx >= 0 ? (
          <span className="rounded-full bg-[var(--k-color-accent-soft)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--k-color-text-link)]">
            Stufe {activeIdx + 1} / {steps.length}
          </span>
        ) : null}
      </div>
      <ol role="list" aria-label="Workflow-Schritte" className="space-y-1">
        {steps.map((step, idx) => {
          const isActive = step.id === activeStepId;
          const isDone = activeIdx >= 0 && idx < activeIdx;
          return (
            <li key={step.id} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={[
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  isActive
                    ? 'bg-[var(--k-color-accent)] text-white'
                    : isDone
                      ? 'bg-[var(--k-color-accent-soft)] text-[var(--k-color-text-link)]'
                      : 'bg-[var(--k-color-surface)] text-[var(--k-color-text-muted)]',
                ].join(' ')}
              >
                {idx + 1}
              </span>
              <span
                aria-current={isActive ? 'step' : undefined}
                className={[
                  'flex-1 text-[12px] font-semibold',
                  isActive
                    ? 'text-[var(--k-color-text-link)]'
                    : 'text-[var(--k-color-text-secondary)]',
                ].join(' ')}
              >
                {step.label}
              </span>
              {isActive ? (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-[var(--k-color-accent)]"
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-1.5 px-1 text-[10px] leading-snug text-[var(--k-color-text-faint)]">
        {activeStepId
          ? `Aktive Stufe: ${steps.find((s) => s.id === activeStepId)?.label.toUpperCase()}`
          : 'Wähle einen Schritt in der Sidebar.'}
      </p>
    </section>
  );
}
